import { useState, useRef, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { IconMinus, IconPlus, IconRotateClockwise, IconFlipVertical, IconCrop, IconCheck, IconMaximize, IconAspectRatio, IconLoader2 } from '@tabler/icons-react';
import { useColorScheme } from '../../../lib/hooks';
import Carousel from '../../../components/common/EmblaCarousel';
import EditorModalShell, { EditorBarFrame, EditorBarTools, EditorBarSecondary, EditorBarPrimary } from '../../../components/common/EditorModalShell';

// Default output aspect (matches the fixed 512×640 portrait export). Callers can
// override via the `aspect` / `outWidth` / `outHeight` props (e.g. the landscape
// "one photo of two people" dual box). The crop box is locked to this ratio so
// dragging any corner moves two sides at once and keeps the ratio.
const DEFAULT_ASPECT = 512 / 640;
const BRAND = '#E68A00';

// Progressive JPEG compression. Starts HIGH and only steps down if the file is over
// budget — the cropped photo is the AI's source material, so we keep it sharp (the old
// 0.85→0.50 floor visibly softened faces). Budget stays under the 500KB upload cap so
// the send path never re-compresses it.
const canvasToCompressedBlob = (canvas, maxBytes = 900 * 1024) => new Promise((resolve) => {
    let quality = 0.95;
    const attempt = () => canvas.toBlob((blob) => {
        if (blob.size <= maxBytes || quality <= 0.72) { resolve(blob); }
        else { quality = Math.round((quality - 0.06) * 100) / 100; attempt(); }
    }, 'image/jpeg', quality);
    attempt();
});

// A caller's `onApply` may be async (the result crop uploads to R2). Wait for it so the button can stay in its loading
// state until the work is really done. A rejection is the caller's to report — it shows its own error and leaves this stage
// open so the crop can be retried — so it is swallowed here instead of closing the stage through `onError`.
const settled = (result) => (typeof result?.then === 'function' ? result.then(() => {}, () => {}) : undefined);

/**
 * Full-screen crop + rotate stage for the Generate-Image uploader. Rendered inside
 * the page's <Modal> (full-screen on both mobile and desktop). It draws its OWN
 * header and footer so the layout — header / crop-canvas / action bar — fills the
 * viewport and the crop area stays large even on small-resolution monitors.
 *
 * All the fast-changing drag/zoom/rotation state lives HERE (not in the huge parent
 * page) so a pointer-drag only re-renders this small component and the crop
 * rectangle tracks the cursor without lag.
 *
 * The parent triggers the crop via the imperative `apply()` handle (used by the
 * modal's close button) or the user clicks the Crop button directly.
 */
const CropStage = forwardRef(function CropStage(
    { imageSrc, fileName, isMobile, compact = false, t, onClose, onApply, onSkip, onError,
      aspect: aspectProp = DEFAULT_ASPECT, outWidth = 512, outHeight = 640, maxBytes = 900 * 1024, outMaxDim = 1600, hint, focusFace = false,
      showSkip = false,
      // Start in free crop (whole image selected) instead of the aspect-locked box. Off by default.
      defaultFreeCrop = false,
      // Optional starting selection (react-image-crop PERCENT crop) — used to re-open a photo with its
      // previous crop area. `onApply` then receives { crop } (the applied percent crop) as a 2nd argument.
      initialCrop = null,
      // Optional ratio presets (chips in the header). When set, the aspect is switchable.
      ratios = null, defaultRatioId,
      title, primaryLabel,
      // Label of the primary button while `onApply` is still running (e.g. "Saving…" for the result crop's upload). Without it
      // the normal label stays and only the icon turns into a spinner.
      primaryLoadingLabel,
      // 'file' (default): downscaled + compressed JPEG File (upload/AI). 'blobFull': full-res,
      // source-format Blob, no downscale/compress (result crop → R2 replace).
      outputMode = 'file', mimeOut = 'image/jpeg' },
    ref,
) {
    // Hint text over the crop canvas. Callers can override (or pass '' to hide).
    const hintText = hint !== undefined ? hint : t('imageGeneration.cropHint');
    const [crop, setCrop] = useState();                 // percent crop (responsive)
    const [completedCrop, setCompletedCrop] = useState(null); // pixel crop for output
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);        // 0 | 90 | 180 | 270
    const [mirror, setMirror] = useState(false);        // horizontal flip
    // The primary button is busy from the click until `onApply` has settled. The ref is the synchronous re-entry guard: a click
    // plus the Enter key (or a double click) land before the next render could disable the button.
    const [applying, setApplying] = useState(false);
    const applyingRef = useRef(false);
    const primaryText = applying && primaryLoadingLabel ? primaryLoadingLabel : (primaryLabel || t('imageGeneration.cropApply'));
    // Free crop (opt-in): OFF by default → the crop box is locked to the aspect below.
    // Toggling ON unlocks it so the user can drag any corner independently and crop to
    // any shape they want. Toggling a ratio preset (or turning this OFF) re-locks it.
    const [freeCrop, setFreeCrop] = useState(defaultFreeCrop);
    // Aspect is switchable when `ratios` presets are provided; otherwise fixed to the prop.
    const [aspect, setAspect] = useState(ratios ? (ratios.find(r => r.id === defaultRatioId)?.value ?? ratios[0]?.value) : aspectProp);
    const [activeRatioId, setActiveRatioId] = useState(ratios ? (defaultRatioId || ratios[0]?.id) : null);
    const [workingSrc, setWorkingSrc] = useState(null); // original BAKED with rotation+mirror
    const imgRef = useRef(null);
    const cropRef = useRef(null);          // latest percent crop (reported to onApply for re-opening later)
    const initialAppliedRef = useRef(false);
    const areaRef = useRef(null);
    const origImgRef = useRef(null);   // the loaded ORIGINAL image (source of truth)
    const workUrlRef = useRef(null);   // current working object-URL (revoked on change)

    // Rotation/mirror are BAKED into an offscreen working image rather than applied as a
    // CSS transform on the displayed <img>. A CSS transform doesn't change the element's
    // layout box, so react-image-crop's overlay drifts out of alignment and the rotated
    // image overflows the container and gets clipped (the reported top/bottom cut). Baking
    // keeps the displayed image correctly oriented so the crop stays aligned and nothing
    // is cut off. Zoom stays a CSS scale (handled in doApply).
    const bake = useCallback((rot, mir) => {
        const img = origImgRef.current;
        if (!img) return;
        const w = img.naturalWidth, h = img.naturalHeight;
        const swap = rot === 90 || rot === 270;
        const canvas = document.createElement('canvas');
        canvas.width = swap ? h : w;
        canvas.height = swap ? w : h;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingQuality = 'high';
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((rot * Math.PI) / 180);
        if (mir) ctx.scale(-1, 1);
        ctx.drawImage(img, -w / 2, -h / 2, w, h);
        // JPEG encodes/decodes far faster than PNG — the working image is only the crop
        // SOURCE (the final output is re-encoded in doApply), so a single high-quality
        // JPEG pass here keeps rotate/mirror snappy without visible quality loss.
        canvas.toBlob((blob) => {
            if (!blob) return;
            if (workUrlRef.current) URL.revokeObjectURL(workUrlRef.current);
            const url = URL.createObjectURL(blob);
            workUrlRef.current = url;
            setWorkingSrc(url);
        }, 'image/jpeg', 0.95);
    }, []);

    // Load the original once, then bake the initial (unrotated) working image.
    useEffect(() => {
        if (!imageSrc) return;
        let cancelled = false;
        const img = new window.Image();
        img.onload = () => { if (!cancelled) { origImgRef.current = img; setRotation(0); setMirror(false); bake(0, false); } };
        img.onerror = () => { if (!cancelled) onError?.(); };
        img.src = imageSrc;
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageSrc]);

    // Re-bake whenever rotation / mirror changes.
    useEffect(() => {
        if (origImgRef.current) bake(rotation, mirror);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rotation, mirror]);

    useEffect(() => () => { if (workUrlRef.current) URL.revokeObjectURL(workUrlRef.current); }, []);

    // Theme-aware surface colors for the header/footer (crop canvas stays black in
    // both modes — photos read best against it). Flips live via the MutationObserver
    // inside useColorScheme when the user toggles the app theme.
    const isDark = useColorScheme() === 'dark';
    const C = isDark
        ? { bar: '#18181b', border: '#3f3f46', text: '#f4f4f5', muted: '#a1a1aa', chip: 'rgba(255,255,255,0.08)', chipBorder: 'rgba(255,255,255,0.12)' }
        : { bar: '#ffffff', border: '#e4e4e7', text: '#27272a', muted: '#71717a', chip: '#f4f4f5', chipBorder: '#e4e4e7' };
    // Site emerald (success) palette for the secondary footer controls (zoom /
    // rotate) — keeps brand orange for the primary Crop action, red for close.
    const ctrlBg = isDark ? '#10b981' : '#059669';

    // Mouse-wheel zoom over the crop area: scroll up → zoom in, down → zoom out.
    // Attached natively with { passive: false } so preventDefault() actually stops
    // the modal/page from scrolling (React's synthetic onWheel is passive).
    useEffect(() => {
        const el = areaRef.current;
        if (!el) return;
        const onWheel = (e) => {
            e.preventDefault();
            const step = e.deltaY < 0 ? 0.05 : -0.05;
            setZoom(z => Math.min(3, Math.max(1, +(z + step).toFixed(2))));
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, []);

    // Seed a centered, aspect-locked selection once the image size is known, and
    // prime the pixel crop so Apply / close-to-apply work without a first drag.
    // Seed a centered selection for the given aspect. focusFace starts tighter on the
    // upper-centre (portrait face) since the AI only gets a small box per face.
    const seedCrop = useCallback((width, height, asp) => {
        let pct;
        if (asp) {
            pct = centerCrop(makeAspectCrop({ unit: '%', width: 88 }, asp, width, height), width, height);
        } else if (focusFace) {
            const w = 62, h = 66;
            pct = { unit: '%', x: (100 - w) / 2, y: 6, width: w, height: h };
        } else {
            pct = centerCrop({ unit: '%', x: 0, y: 0, width: 100, height: 100 }, width, height);
        }
        setCrop(pct);
        setCompletedCrop({
            unit: 'px',
            x: (pct.x / 100) * width,
            y: (pct.y / 100) * height,
            width: (pct.width / 100) * width,
            height: (pct.height / 100) * height,
        });
    }, [focusFace]);

    useEffect(() => { cropRef.current = crop; }, [crop]);

    const onImageLoad = useCallback((e) => {
        const { width, height } = e.currentTarget;
        // First load of a re-opened photo: start from the crop area the user chose last time.
        if (initialCrop && initialCrop.unit === '%' && !initialAppliedRef.current) {
            initialAppliedRef.current = true;
            setCrop(initialCrop);
            setCompletedCrop({
                unit: 'px',
                x: (initialCrop.x / 100) * width,
                y: (initialCrop.y / 100) * height,
                width: (initialCrop.width / 100) * width,
                height: (initialCrop.height / 100) * height,
            });
            return;
        }
        seedCrop(width, height, freeCrop ? null : aspect);
    }, [seedCrop, aspect, freeCrop, initialCrop]);

    // Ratio-preset chip click → switch aspect + reseed the selection (also re-locks
    // the box, turning free crop off).
    const pickRatio = useCallback((r) => {
        setActiveRatioId(r.id);
        setAspect(r.value);
        setFreeCrop(false);
        const img = imgRef.current;
        if (img) seedCrop(img.width, img.height, r.value);
    }, [seedCrop]);

    // Free-crop toggle → unlock/re-lock the crop box and reseed for the new mode.
    const toggleFreeCrop = useCallback(() => {
        setFreeCrop((prev) => {
            const next = !prev;
            const img = imgRef.current;
            if (img) seedCrop(img.width, img.height, next ? null : aspect);
            return next;
        });
    }, [seedCrop, aspect]);

    const doApply = useCallback(async () => {
        if (applyingRef.current) return;
        try {
            const image = imgRef.current;
            // No usable selection yet → fall back to committing the original file (or just close).
            if (!image || !completedCrop?.width || !completedCrop?.height) {
                if (onSkip) onSkip(); else onClose?.();
                return;
            }

            applyingRef.current = true;
            setApplying(true);
            // Let the button repaint as "working" before the canvas work below, which blocks the page for a moment on a big photo.
            await new Promise((resolve) => setTimeout(resolve, 50));

            // The crop rect is in *displayed* px; scale up to natural resolution.
            const scaleX = image.naturalWidth / image.width;
            const scaleY = image.naturalHeight / image.height;
            const cropWNat = Math.round(completedCrop.width * scaleX);
            const cropHNat = Math.round(completedCrop.height * scaleY);

            // Step 1: draw the selected region, replicating the on-screen zoom
            // (CSS scale) and rotation about the image center.
            const rawCanvas = document.createElement('canvas');
            rawCanvas.width = cropWNat;
            rawCanvas.height = cropHNat;
            const rawCtx = rawCanvas.getContext('2d');
            rawCtx.imageSmoothingQuality = 'high';
            const cropX = completedCrop.x * scaleX;
            const cropY = completedCrop.y * scaleY;
            const centerX = image.naturalWidth / 2;
            const centerY = image.naturalHeight / 2;
            // Rotation + mirror are already BAKED into the displayed image, so only the
            // on-screen zoom (CSS scale, about the image centre) needs replicating here.
            rawCtx.save();
            rawCtx.translate(-cropX, -cropY);
            rawCtx.translate(centerX, centerY);
            rawCtx.scale(zoom, zoom);
            rawCtx.translate(-centerX, -centerY);
            rawCtx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight);
            rawCtx.restore();

            // blobFull: full-resolution, source-format, near-lossless (no downscale/compress) —
            // used by the result crop that overwrites the stored R2 image in place.
            if (outputMode === 'blobFull') {
                const type = ['image/png', 'image/webp', 'image/jpeg'].includes(mimeOut) ? mimeOut : 'image/jpeg';
                const q = type === 'image/png' ? undefined : 0.97;
                const blob = await new Promise((res) => rawCanvas.toBlob(res, type, q));
                await settled(onApply(blob, { width: cropWNat, height: cropHNat }));
                return;
            }

            // Step 2: scale to the output size. The passed outWidth/outHeight (e.g.
            // 512×640) is only an ASPECT anchor — exporting at that tiny size throws away
            // face/hair detail before the backend ever sees it. Export at up to SUPERSCALE×
            // that size so the AI gets a sharp source (the server still downsamples to its
            // own 342px input box — token cost is unchanged — but from a cleaner image).
            // Capped at the crop's natural pixels so a small source is never upscaled.
            // Export the crop at its NATIVE resolution, capped only at `outMaxDim`.
            // The passed outWidth/outHeight (512×640) is just an aspect anchor —
            // exporting at that tiny size is exactly what softened the uploaded photo.
            // The crop rect is already aspect-locked, so native pixels keep both the
            // shape and the full detail. The AI editor keeps the smaller 1600px default
            // (its upload budget is tight); the manual editor raises it, because there
            // the crop output IS the photo the user prints.
            const outCanvas = document.createElement('canvas');
            const s = Math.min(1, outMaxDim / Math.max(cropWNat, cropHNat));
            outCanvas.width = Math.max(1, Math.round(cropWNat * s));
            outCanvas.height = Math.max(1, Math.round(cropHNat * s));
            const outCtx = outCanvas.getContext('2d');
            outCtx.imageSmoothingEnabled = true;
            outCtx.imageSmoothingQuality = 'high';
            outCtx.drawImage(rawCanvas, 0, 0, outCanvas.width, outCanvas.height);

            const blob = await canvasToCompressedBlob(outCanvas, maxBytes);
            await settled(onApply(new File([blob], fileName || 'photo.jpg', { type: 'image/jpeg' }), { crop: cropRef.current }));
        } catch {
            onError();
        } finally {
            applyingRef.current = false;
            setApplying(false);
        }
    }, [completedCrop, rotation, zoom, fileName, aspect, outWidth, outHeight, maxBytes, outMaxDim, outputMode, mimeOut, onApply, onSkip, onClose, onError]);

    useImperativeHandle(ref, () => ({ apply: doApply }), [doApply]);

    // Keyboard shortcuts: Enter → crop (apply), Escape → close the modal.
    useEffect(() => {
        const onKey = (e) => {
            const tag = e.target?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA') return; // don't hijack typing
            if (e.key === 'Enter') {
                e.preventDefault();
                doApply();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [doApply, onClose]);

    return (
        <>
            {/* Branded, larger corner/edge handles for the resizable crop box */}
            <style>{`
                .sk-crop .ReactCrop__drag-handle::after {
                    width: 14px; height: 14px; border-radius: 50%;
                    background: ${BRAND}; border: 2px solid #fff;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.4);
                    transform: translate(-2px, -2px);
                }
                .sk-crop .ReactCrop__crop-selection { border-radius: 6px; }
            `}</style>

            {/* Shared editor-modal DESIGN shell; the crop functionality (canvas content +
                bottom toolbar) stays here and is passed in dynamically. */}
            <EditorModalShell
                isMobile={isMobile}
                compact={compact}
                icon={<IconCrop size={20} />}
                title={title || t('imageGeneration.cropModalTitle')}
                onClose={onClose}
                canvasRef={areaRef}
                headerExtra={ratios ? (
                    /* Ratio presets — chips beside the title; they drag / swipe sideways when they don't fit. */
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: isMobile ? 6 : 14, flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: C.muted, flexShrink: 0 }}>{t('imageGeneration.cropRatioLabel')}:</span>
                        <Carousel slideGap={6} style={{ flex: 1, minWidth: 0 }}>
                        {ratios.map((r) => (
                            <Carousel.Slide key={r.id}>
                            <button
                                onClick={() => pickRatio(r)}
                                style={{ padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap', border: `1.5px solid ${activeRatioId === r.id ? BRAND : C.chipBorder}`, background: activeRatioId === r.id ? 'rgba(230,138,0,0.14)' : C.chip, color: activeRatioId === r.id ? BRAND : C.muted }}
                            >{r.label}</button>
                            </Carousel.Slide>
                        ))}
                        </Carousel>
                    </div>
                ) : null}
                canvas={<>
                {hintText && (
                    <p style={{ position: 'absolute', top: isMobile ? 6 : 10, left: 0, right: 0, textAlign: 'center', color: 'rgba(255,255,255,0.55)', fontSize: isMobile ? 12 : 13, zIndex: 1, pointerEvents: 'none', padding: '0 16px' }}>
                        {hintText}
                    </p>
                )}
                {workingSrc && (
                    <ReactCrop
                        className="sk-crop"
                        crop={crop}
                        aspect={freeCrop ? undefined : (aspect || undefined)}
                        keepSelection
                        ruleOfThirds
                        minWidth={40}
                        onChange={(_px, pct) => setCrop(pct)}
                        onComplete={(px) => setCompletedCrop(px)}
                        style={{ maxWidth: '100%', maxHeight: '100%' }}
                    >
                        <img
                            ref={imgRef}
                            src={workingSrc}
                            alt=""
                            onLoad={onImageLoad}
                            onError={onError}
                            style={{
                                display: 'block',
                                // Contain inside the crop area: cap height to the viewport
                                // minus the header + footer chrome (a %-height here would
                                // resolve against ReactCrop's auto-height wrapper and let
                                // the image overflow), and cap width to the area.
                                maxHeight: isMobile ? 'calc(100vh - 210px)' : compact ? 'calc(100vh - 150px)' : 'calc(100vh - 200px)',
                                maxWidth: '100%',
                                width: 'auto',
                                height: 'auto',
                                objectFit: 'contain',
                                // Rotation/mirror are baked into workingSrc; only zoom is a live CSS transform.
                                transform: `scale(${zoom})`,
                                transition: 'transform 0.15s ease',
                            }}
                        />
                    </ReactCrop>
                )}
                </>}
                bottomBar={
            /* ── Bottom action bar — standardized frame/blocks from the shell ── */
            <EditorBarFrame isMobile={isMobile} compact={compact}>
                {isMobile ? (
                    /* Mobile: optional "take whole image" button + 4-button row */
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {showSkip && (
                        <button
                            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '11px 6px', borderRadius: 14, background: C.chip, border: `1px solid ${C.chipBorder}`, color: C.text, cursor: 'pointer' }}
                            onClick={onSkip}
                        >
                            <IconMaximize size={18} />
                            <span style={{ fontSize: 13, fontWeight: 600 }}>{t('imageGeneration.cropSkip')}</span>
                        </button>
                    )}
                    <div style={{ display: 'flex', gap: 10 }}>
                        <button
                            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '10px 6px', borderRadius: 14, background: C.chip, border: `1px solid ${C.chipBorder}`, color: C.text, cursor: 'pointer' }}
                            onClick={() => setZoom(z => Math.max(1, +(z - 0.1).toFixed(2)))}
                        >
                            <IconMinus size={18} />
                            <span style={{ fontSize: 10, fontWeight: 600 }}>{t('imageGeneration.cropZoomOut')}</span>
                        </button>
                        <button
                            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '10px 6px', borderRadius: 14, background: C.chip, border: `1px solid ${C.chipBorder}`, color: C.text, cursor: 'pointer' }}
                            onClick={() => setZoom(z => Math.min(3, +(z + 0.1).toFixed(2)))}
                        >
                            <IconPlus size={18} />
                            <span style={{ fontSize: 10, fontWeight: 600 }}>{t('imageGeneration.cropZoomIn')}</span>
                        </button>
                        <button
                            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '10px 6px', borderRadius: 14, background: C.chip, border: `1px solid ${C.chipBorder}`, color: C.text, cursor: 'pointer' }}
                            onClick={() => setRotation(r => (r + 90) % 360)}
                        >
                            <IconRotateClockwise size={18} />
                            <span style={{ fontSize: 10, fontWeight: 600 }}>{t('imageGeneration.cropRotate')}</span>
                        </button>
                        <button
                            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '10px 6px', borderRadius: 14, background: mirror ? 'rgba(230,138,0,0.15)' : C.chip, border: `1px solid ${mirror ? BRAND : C.chipBorder}`, color: mirror ? BRAND : C.text, cursor: 'pointer' }}
                            onClick={() => setMirror(m => !m)}
                        >
                            <IconFlipVertical size={18} />
                            <span style={{ fontSize: 10, fontWeight: 600 }}>{t('imageGeneration.cropMirror')}</span>
                        </button>
                        <button
                            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '10px 6px', borderRadius: 14, background: freeCrop ? 'rgba(230,138,0,0.15)' : C.chip, border: `1px solid ${freeCrop ? BRAND : C.chipBorder}`, color: freeCrop ? BRAND : C.text, cursor: 'pointer' }}
                            onClick={toggleFreeCrop}
                        >
                            <IconAspectRatio size={18} />
                            <span style={{ fontSize: 10, fontWeight: 600 }}>{t('imageGeneration.cropFreeToggle')}</span>
                        </button>
                        <button
                            style={{ flex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '10px 6px', borderRadius: 14, background: completedCrop ? 'linear-gradient(135deg,#FBA53A,#E68A00)' : C.chip, border: 'none', color: completedCrop ? '#fff' : C.muted, cursor: applying ? 'progress' : completedCrop ? 'pointer' : 'not-allowed', fontWeight: 700 }}
                            onClick={doApply}
                            disabled={!completedCrop || applying}
                            aria-busy={applying || undefined}
                        >
                            {applying ? <IconLoader2 size={20} className="animate-spin" /> : <IconCheck size={20} strokeWidth={2.5} />}
                            <span style={{ fontSize: 11, fontWeight: 700 }}>{primaryText}</span>
                        </button>
                    </div>
                    </div>
                ) : (
                    /* Desktop: zoom + rotate pinned bottom-left, primary Crop as a
                       full-height block bottom-right — both absolute like the close button. */
                    <>
                        <EditorBarTools>
                            <button
                                style={{ width: compact ? 56 : 64, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: ctrlBg, color: '#fff', border: 'none', borderRadius: 0, cursor: 'pointer' }}
                                onClick={() => setZoom(z => Math.max(1, +(z - 0.05).toFixed(2)))}
                                aria-label={t('imageGeneration.cropZoomOut')}
                            >
                                <IconMinus size={20} strokeWidth={2.5} />
                            </button>
                            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 54, padding: '0 6px', fontSize: 13, fontWeight: 700, color: C.text }}>{Math.round(zoom * 100)}%</span>
                            <button
                                style={{ width: compact ? 56 : 64, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: ctrlBg, color: '#fff', border: 'none', borderRadius: 0, cursor: 'pointer' }}
                                onClick={() => setZoom(z => Math.min(3, +(z + 0.05).toFixed(2)))}
                                aria-label={t('imageGeneration.cropZoomIn')}
                            >
                                <IconPlus size={20} strokeWidth={2.5} />
                            </button>
                            <button
                                className="inline-flex items-center gap-1.5 font-semibold text-sm"
                                style={{ marginLeft: 12, padding: '0 20px', background: ctrlBg, color: '#fff', border: 'none', borderRadius: 0, cursor: 'pointer' }}
                                onClick={() => setRotation(r => (r + 90) % 360)}
                            >
                                <IconRotateClockwise size={18} /> {t('imageGeneration.cropRotate')}
                            </button>
                            <button
                                className="inline-flex items-center gap-1.5 font-semibold text-sm"
                                style={{ marginLeft: 12, padding: '0 20px', background: mirror ? BRAND : '#6366f1', color: '#fff', border: 'none', borderRadius: 0, cursor: 'pointer' }}
                                onClick={() => setMirror(m => !m)}
                            >
                                <IconFlipVertical size={18} /> {t('imageGeneration.cropMirror')}
                            </button>
                            <button
                                className="inline-flex items-center gap-1.5 font-semibold text-sm"
                                style={{ marginLeft: 12, padding: '0 20px', background: freeCrop ? BRAND : '#0d9488', color: '#fff', border: 'none', borderRadius: 0, cursor: 'pointer' }}
                                onClick={toggleFreeCrop}
                                title={t('imageGeneration.cropFreeHint')}
                            >
                                <IconAspectRatio size={18} /> {t('imageGeneration.cropFreeToggle')}
                            </button>
                        </EditorBarTools>
                        {showSkip && (
                            <EditorBarSecondary compact={compact} onClick={onSkip} title={t('imageGeneration.cropSkip')}>
                                <IconMaximize size={18} /> {t('imageGeneration.cropSkip')}
                            </EditorBarSecondary>
                        )}
                        <EditorBarPrimary compact={compact} onClick={doApply} disabled={!completedCrop} loading={applying}>
                            {applying ? <IconLoader2 size={18} className="animate-spin" /> : <IconCrop size={18} />} {primaryText}
                        </EditorBarPrimary>
                    </>
                )}
            </EditorBarFrame>
                }
            />
        </>
    );
});

export default CropStage;
